// src/components/requests/ProviderView.jsx - COMPLETE WORKING VERSION
import React, { useState, useEffect } from 'react';
import { ShoppingCart, CreditCard, Zap, Plus, Edit2, Trash2, Copy } from 'lucide-react';
import { requestsService } from '../../services/requestsService';
import ProviderProfile from './ProviderProfile';
import UniversalField from '../shared/forms/UniversalField';

const ProviderView = ({ bookId, user, isDarkMode, bookData }) => {
  const [isRegistered, setIsRegistered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeRequests, setActiveRequests] = useState([]);
  const [sentOffers, setSentOffers] = useState([]);
  const [presetOffers, setPresetOffers] = useState([]);
  
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedCredits, setSelectedCredits] = useState(0);
  
  const providerConfig = {
    fields: bookData?.providerFields || [],
    categories: bookData?.providerCategories || ["General Services"],
    settings: bookData?.providerSettings || {}
  };

  const creditsConfig = bookData?.creditsSettings || {
    costPerOffer: 5,
    costPerResponse: 1,
    freeCredits: 50,
    allowAdditionalPurchase: true,
    packages: {
      basic: { credits: 100, price: 100 },
      standard: { credits: 250, price: 225 },
      premium: { credits: 500, price: 400 }
    }
  };

  const [activeTab, setActiveTab] = useState('requests');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isOfferModalOpen, setIsOfferModalOpen] = useState(false);
  const [isSendingOffer, setIsSendingOffer] = useState(false);
  const [offerForm, setOfferForm] = useState({ valueProp: '', price: '', timeframe: '' });
  const [selectedPreset, setSelectedPreset] = useState(null);

  const [showPresetModal, setShowPresetModal] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);
  const [presetForm, setPresetForm] = useState({
    name: '',
    category: '',
    valueProp: '',
    price: '',
    timeframe: '',
    description: '',
    autoRespond: false
  });

  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [budget, setBudget] = useState({
    credits: creditsConfig.freeCredits,
    dailyLimit: 50,
    weeklyLimit: 200,
    spentToday: 0,
    spentWeek: 0
  });
  const [tempLimits, setTempLimits] = useState({ daily: 50, weekly: 200 });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [editFormErrors, setEditFormErrors] = useState({});
  const [editSelectedCategories, setEditSelectedCategories] = useState([]);
  const [savingProfile, setSavingProfile] = useState(false);
  const [currentProfile, setCurrentProfile] = useState(null);

  const providerId = user.uid;

  useEffect(() => {
    const initialData = {};
    providerConfig.fields.forEach(field => {
      if (field.name !== 'category') {
        initialData[field.name] = '';
      }
    });
    setFormData(initialData);
    setSelectedCredits(creditsConfig.freeCredits);
  }, []);

  useEffect(() => {
    if (isRegistered) {
      loadPresetOffers();
    }
  }, [isRegistered]);

  useEffect(() => {
    checkRegistrationStatus();
  }, [bookId, providerId]);

  const checkRegistrationStatus = async () => {
    setLoading(true);
    try {
      const existingProfile = await requestsService.getProviderProfileByUserId(bookId, providerId);
      
      if (existingProfile) {
        console.log('Existing provider found:', existingProfile);
        
        setCurrentProfile(existingProfile); // Save the full profile
        
        setFormData({
          businessName: existingProfile.businessName || '',
          description: existingProfile.description || '',
          email: existingProfile.email || '',
          phone: existingProfile.phone || '',
          website: existingProfile.website || '',
          logo: existingProfile.logo || ''
        });
        
        setSelectedCategories(existingProfile.categories || []);
        setIsRegistered(true);
        
        await Promise.all([
          loadPresetOffers(),
          loadSentOffers(),
          loadAvailableRequests()
        ]);
      } else {
        console.log('No existing provider profile found');
        setIsRegistered(false);
      }
    } catch (error) {
      console.error('Error checking registration status:', error);
      setIsRegistered(false);
    } finally {
      setLoading(false);
    }
  };

  // ADD: Open edit mode
  const startEditingProfile = () => {
    setEditFormData({ ...formData });
    setEditSelectedCategories([...selectedCategories]);
    setIsEditingProfile(true);
  };

  // ADD: Cancel editing
  const cancelEditingProfile = () => {
    setEditFormData({});
    setEditSelectedCategories([]);
    setEditFormErrors({});
    setIsEditingProfile(false);
  };

  // ADD: Handle edit field changes
  const handleEditFieldChange = (fieldName, value) => {
    setEditFormData(prev => ({ ...prev, [fieldName]: value }));
    if (editFormErrors[fieldName]) {
      setEditFormErrors(prev => ({ ...prev, [fieldName]: null }));
    }
  };

  // ADD: Toggle edit categories
  const toggleEditCategory = (category) => {
    setEditSelectedCategories(prev => {
      if (prev.includes(category)) {
        return prev.filter(c => c !== category);
      } else if (prev.length < 4) {
        return [...prev, category];
      }
      return prev;
    });
  };

  // ADD: Validate edit form
  const validateEditForm = () => {
    const errors = {};
    providerConfig.fields.forEach(field => {
      if (field.required && field.name !== 'category' && !editFormData[field.name]) {
        errors[field.name] = `${field.label} is required`;
      }
    });
    if (editSelectedCategories.length === 0) {
      errors.category = 'Select at least one category';
    }
    return errors;
  };

  // ADD: Save profile changes
  const saveProfileChanges = async () => {
    const errors = validateEditForm();
    if (Object.keys(errors).length > 0) {
      setEditFormErrors(errors);
      return;
    }

    setSavingProfile(true);
    
    try {
      await requestsService.updateProviderProfile(bookId, currentProfile.id, {
        ...editFormData,
        categories: editSelectedCategories
      });

      // Update local state
      setFormData({ ...editFormData });
      setSelectedCategories([...editSelectedCategories]);
      
      // Reload profile to ensure consistency
      await checkRegistrationStatus();
      
      setIsEditingProfile(false);
      setEditFormData({});
      setEditSelectedCategories([]);
    } catch (error) {
      console.error('Failed to update profile:', error);
      setEditFormErrors({ general: 'Failed to save changes. Please try again.' });
    } finally {
      setSavingProfile(false);
    }
  };

  const loadPresetOffers = async () => {
    try {
      const presets = await requestsService.getProviderPresets(bookId, providerId);
      setPresetOffers(presets);
    } catch (error) {
      console.error('Failed to load preset offers:', error);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    if (formErrors[fieldName]) {
      setFormErrors(prev => ({ ...prev, [fieldName]: null }));
    }
  };

  const toggleCategory = (category) => {
    setSelectedCategories(prev => {
      if (prev.includes(category)) {
        return prev.filter(c => c !== category);
      } else if (prev.length < 4) {
        return [...prev, category];
      }
      return prev;
    });
  };

  const loadSentOffers = async () => {
    try {
      const offers = await requestsService.getOffersByProvider(bookId, providerId);
      setSentOffers(offers);
    } catch (error) {
      console.error('Failed to load sent offers:', error);
    }
  };

  const loadAvailableRequests = async () => {
    try {
      const requests = await requestsService.getOpenRequests(bookId);
      setActiveRequests(requests);
    } catch (error) {
      console.error('Failed to load requests:', error);
    }
  };

  const validateForm = () => {
    const errors = {};
    providerConfig.fields.forEach(field => {
      if (field.required && field.name !== 'category' && !formData[field.name]) {
        errors[field.name] = `${field.label} is required`;
      }
    });
    if (selectedCategories.length === 0) {
      errors.category = 'Select at least one category';
    }
    return errors;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setLoading(true);
    
    try {
      await requestsService.createProviderProfile(bookId, providerId, {
        ...formData,
        categories: selectedCategories,
        verified: !providerConfig.settings.requireApproval,
        featured: false,
        createdAt: new Date()
      });

      setBudget(prev => ({ ...prev, credits: selectedCredits }));

      const requests = await requestsService.getOpenRequests(bookId);
      setActiveRequests(requests);
      
      setIsRegistered(true);
    } catch (error) {
      console.error('Registration failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const openPresetModal = (preset = null) => {
    if (preset) {
      setEditingPreset(preset);
      setPresetForm({
        name: preset.name,
        category: preset.category,
        valueProp: preset.valueProp,
        price: preset.price,
        timeframe: preset.timeframe,
        description: preset.description || '',
        autoRespond: preset.autoRespond || false
      });
    } else {
      setEditingPreset(null);
      setPresetForm({
        name: '',
        category: selectedCategories[0] || '',
        valueProp: '',
        price: '',
        timeframe: '',
        description: '',
        autoRespond: false
      });
    }
    setShowPresetModal(true);
  };

  const savePreset = async () => {
    try {
      if (editingPreset) {
        await requestsService.updatePresetOffer(bookId, editingPreset.id, presetForm);
      } else {
        await requestsService.createPresetOffer(bookId, providerId, presetForm);
      }
      await loadPresetOffers();
      setShowPresetModal(false);
    } catch (error) {
      console.error('Failed to save preset:', error);
    }
  };

  const deletePreset = async (presetId) => {
    if (!window.confirm('Delete this preset offer?')) return;
    try {
      await requestsService.deletePresetOffer(bookId, presetId);
      await loadPresetOffers();
    } catch (error) {
      console.error('Failed to delete preset:', error);
    }
  };

  const toggleAutoRespond = async (preset) => {
    try {
      await requestsService.updatePresetOffer(bookId, preset.id, {
        autoRespond: !preset.autoRespond
      });
      await loadPresetOffers();
    } catch (error) {
      console.error('Failed to toggle auto-respond:', error);
    }
  };

  const openOfferModal = (request, preset = null) => {
    if (budget.credits < creditsConfig.costPerOffer) {
      alert("Insufficient credits. Please top up to continue.");
      setShowBudgetModal(true);
      return;
    }
    if (budget.dailyLimit > 0 && (budget.spentToday + creditsConfig.costPerOffer) > budget.dailyLimit) {
      alert(`Daily spending limit reached.`);
      setShowBudgetModal(true);
      return;
    }
    if (budget.weeklyLimit > 0 && (budget.spentWeek + creditsConfig.costPerOffer) > budget.weeklyLimit) {
      alert(`Weekly spending limit reached.`);
      setShowBudgetModal(true);
      return;
    }

    setSelectedRequest(request);
    
    if (preset) {
      setOfferForm({
        valueProp: preset.valueProp,
        price: preset.price,
        timeframe: preset.timeframe
      });
      setSelectedPreset(preset);
    } else {
      setOfferForm({
        valueProp: `Hi ${request.userFirstName || 'there'}, I'd love to help with "${request.title}".`,
        price: '',
        timeframe: ''
      });
      setSelectedPreset(null);
    }
    
    setIsOfferModalOpen(true);
  };

  console.log('Selected Request for Offer:', selectedRequest);

  const applyPresetToOffer = (preset) => {
    setOfferForm({
      valueProp: preset.valueProp,
      price: preset.price,
      timeframe: preset.timeframe
    });
    setSelectedPreset(preset);
  };

  const sendOffer = async () => {
    setIsSendingOffer(true);
    try {
      if (selectedPreset) {
        await requestsService.usePresetOffer(bookId, selectedRequest.id, providerId, selectedPreset.id);
      } else {
        await requestsService.createOffer(bookId, selectedRequest.id, providerId, {
          ...offerForm,
          providerName: formData.businessName,
          providerCategories: selectedCategories,
          status: 'pending',
          createdAt: new Date()
        });
      }

      setBudget(prev => ({
        ...prev,
        credits: prev.credits - creditsConfig.costPerOffer,
        spentToday: prev.spentToday + creditsConfig.costPerOffer,
        spentWeek: prev.spentWeek + creditsConfig.costPerOffer
      }));

      setSentOffers(prev => [...prev, { ...offerForm, requestId: selectedRequest.id }]);
      setIsOfferModalOpen(false);
      setSelectedPreset(null);
    } catch (error) {
      console.error('Failed to send offer:', error);
    } finally {
      setIsSendingOffer(false);
    }
  };

  const saveBudgetSettings = () => {
    setBudget(prev => ({
      ...prev,
      dailyLimit: tempLimits.daily,
      weeklyLimit: tempLimits.weekly
    }));
    setShowBudgetModal(false);
  };

  if (!isRegistered) {
    return (
      <div className={`min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 ${isDarkMode ? 'dark bg-[#0f172a]' : 'bg-slate-50'} py-12 px-4`}>
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          
          <div className="order-2 md:order-1">
            <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white mb-4 leading-tight">
              Join as a Provider
            </h1>
            <p className="text-lg text-slate-600 dark:text-slate-300 mb-6">
              Let customers come to you. Pay only when you send offers.
            </p>
            
            <div className="mb-6 p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Simple Pricing</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Per Offer Sent</span>
                  <span className="font-semibold text-slate-900 dark:text-white">{creditsConfig.costPerOffer} credits</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Per Response</span>
                  <span className="font-semibold text-slate-900 dark:text-white">{creditsConfig.costPerResponse} credits</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-slate-200 dark:border-slate-700">
                  <span className="text-emerald-600 dark:text-emerald-400">Free on Registration</span>
                  <span className="font-bold text-emerald-600 dark:text-emerald-400">
                    {creditsConfig.freeCredits} credits
                  </span>
                </div>
              </div>
            </div>

            <ul className="space-y-4 mb-8">
              {[
                "Access verified local requests",
                "Control budget with daily limits",
                "No hidden success fees",
                `${creditsConfig.freeCredits} free credits to start`
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-slate-700 dark:text-slate-300">
                  <svg className="w-5 h-5 text-emerald-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="order-1 md:order-2 bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-700 overflow-hidden">
            <div className="p-8">
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Provider Registration</h3>
              
              {providerConfig.settings.requireApproval && (
                <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    Your registration will be reviewed before approval.
                  </p>
                </div>
              )}

              <form onSubmit={handleRegister} className="space-y-5">
                {providerConfig.fields
                  .filter(field => field.name !== 'category')
                  .sort((a, b) => a.order - b.order)
                  .map(field => (
                    <UniversalField
                      key={field.id}
                      field={field}
                      value={formData[field.name]}
                      onChange={(value) => handleFieldChange(field.name, value)}
                      error={formErrors[field.name]}
                    />
                  ))}

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Service Categories (Select up to 4) *
                  </label>
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900">
                    {providerConfig.categories.map(category => (
                      <button
                        key={category}
                        type="button"
                        onClick={() => toggleCategory(category)}
                        disabled={!selectedCategories.includes(category) && selectedCategories.length >= 4}
                        className={`px-3 py-2 text-sm rounded-lg border transition-colors text-left ${
                          selectedCategories.includes(category)
                            ? 'bg-emerald-100 dark:bg-emerald-900/30 border-emerald-500 text-emerald-700 dark:text-emerald-300'
                            : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:border-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed'
                        }`}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    {formErrors.category && (
                      <p className="text-sm text-red-600 dark:text-red-400">{formErrors.category}</p>
                    )}
                    <p className="text-xs text-slate-500 dark:text-slate-400 ml-auto">
                      {selectedCategories.length}/4 selected
                    </p>
                  </div>
                </div>

                {creditsConfig.allowAdditionalPurchase && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Starting Credits
                    </label>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <input
                          type="number"
                          min={creditsConfig.freeCredits}
                          step="50"
                          value={selectedCredits}
                          onChange={(e) => setSelectedCredits(parseInt(e.target.value) || creditsConfig.freeCredits)}
                          className="w-32 px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                        />
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                          credits ({Math.floor(selectedCredits / creditsConfig.costPerOffer)} offers)
                        </span>
                      </div>
                      
                      {selectedCredits > creditsConfig.freeCredits && (
                        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-blue-800 dark:text-blue-200">Additional purchase:</span>
                            <span className="font-semibold text-blue-900 dark:text-blue-100">
                              R{Math.ceil((selectedCredits - creditsConfig.freeCredits) * 
                                (creditsConfig.packages.basic.price / creditsConfig.packages.basic.credits))}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        Includes {creditsConfig.freeCredits} free credits
                      </div>
                    </div>
                  </div>
                )}

                <button 
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-lg shadow-emerald-200 dark:shadow-emerald-900/20 transition-all transform active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Registering...' : 'Start Receiving Requests'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto w-full animate-fade-in relative">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 pb-4 border-b border-slate-200 dark:border-slate-700">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Welcome, {formData.businessName}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm">
            {selectedCategories.join(', ')} • Member since 2025
          </p>
        </div>
        
        <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg mt-4 md:mt-0">
          <button 
            onClick={() => setActiveTab('requests')}
            className={`px-4 md:px-6 py-2 rounded-md text-sm font-semibold transition-all ${
              activeTab === 'requests' 
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm' 
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
            }`}
          >
            Requests <span className="hidden md:inline ml-1 bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-200 px-1.5 py-0.5 rounded-full text-xs">{activeRequests.length}</span>
          </button>
          <button 
            onClick={() => setActiveTab('presets')}
            className={`px-4 md:px-6 py-2 rounded-md text-sm font-semibold transition-all ${
              activeTab === 'presets' 
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm' 
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
            }`}
          >
            Presets <span className="ml-1 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 px-1.5 py-0.5 rounded-full text-xs">{presetOffers.length}</span>
          </button>
          <button 
            onClick={() => setActiveTab('sent')}
            className={`px-4 md:px-6 py-2 rounded-md text-sm font-semibold transition-all ${
              activeTab === 'sent' 
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm' 
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
            }`}
          >
            Sent Offers <span className="ml-1 bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded-full text-xs">{sentOffers.length}</span>
          </button>
          <button 
            onClick={() => setActiveTab('profile')}
            className={`px-4 md:px-6 py-2 rounded-md text-sm font-semibold transition-all ${
              activeTab === 'profile' 
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm' 
                : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
            }`}
          >
            Profile
          </button>
        </div>
      </div>

      {activeTab === 'presets' && (
        <div className="animate-fade-in">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-bold text-slate-900 dark:text-white text-lg">Preset Offers</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">Create quick-response templates for common requests</p>
            </div>
            <button
              onClick={() => openPresetModal()}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <Plus className="h-4 w-4" />
              New Preset
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {presetOffers.map(preset => (
              <div key={preset.id} className="p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-900 dark:text-white">{preset.name}</h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{preset.category}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openPresetModal(preset)}
                      className="p-1.5 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => deletePreset(preset.id)}
                      className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <p className="text-sm text-slate-600 dark:text-slate-400 mb-3 line-clamp-2">
                  {preset.valueProp}
                </p>

                <div className="flex items-center justify-between text-sm mb-3">
                  <span className="text-slate-600 dark:text-slate-400">R{preset.price}</span>
                  <span className="text-slate-600 dark:text-slate-400">{preset.timeframe}</span>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-slate-200 dark:border-slate-700">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preset.autoRespond}
                      onChange={() => toggleAutoRespond(preset)}
                      className="rounded"
                    />
                    <Zap className={`h-4 w-4 ${preset.autoRespond ? 'text-amber-500' : 'text-slate-400'}`} />
                    <span className="text-slate-600 dark:text-slate-400">Auto-respond</span>
                  </label>
                </div>
              </div>
            ))}

            {presetOffers.length === 0 && (
              <div className="col-span-full text-center py-12 bg-slate-50 dark:bg-slate-800/50 rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-700">
                <Zap className="h-12 w-12 text-slate-400 mx-auto mb-3" />
                <p className="text-slate-500 dark:text-slate-400 mb-4">No preset offers yet</p>
                <button
                  onClick={() => openPresetModal()}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Create Your First Preset
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'profile' && (
        <div className="animate-fade-in">
          {!isEditingProfile ? (
            // View Mode
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-slate-900 dark:text-white text-lg">Provider Profile</h3>
                <button
                  onClick={startEditingProfile}
                  className="flex items-center gap-2 px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg hover:bg-slate-800 dark:hover:bg-slate-100"
                >
                  <Edit2 className="h-4 w-4" />
                  Edit Profile
                </button>
              </div>

              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 space-y-6">
                {/* Business Name */}
                <div>
                  <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">
                    Business Name
                  </label>
                  <p className="text-lg font-semibold text-slate-900 dark:text-white">
                    {formData.businessName || 'Not provided'}
                  </p>
                </div>

                {/* Categories */}
                <div>
                  <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-2">
                    Service Categories
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {selectedCategories.map(cat => (
                      <span
                        key={cat}
                        className="px-3 py-1.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded-lg text-sm font-medium"
                      >
                        {cat}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Description */}
                {formData.description && (
                  <div>
                    <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Description
                    </label>
                    <p className="text-slate-700 dark:text-slate-300">
                      {formData.description}
                    </p>
                  </div>
                )}

                {/* Contact Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {formData.email && (
                    <div>
                      <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">
                        Email
                      </label>
                      <p className="text-slate-700 dark:text-slate-300">{formData.email}</p>
                    </div>
                  )}
                  {formData.phone && (
                    <div>
                      <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">
                        Phone
                      </label>
                      <p className="text-slate-700 dark:text-slate-300">{formData.phone}</p>
                    </div>
                  )}
                </div>

                {formData.website && (
                  <div>
                    <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Website
                    </label>
                    <a
                      href={formData.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-600 dark:text-emerald-400 hover:underline"
                    >
                      {formData.website}
                    </a>
                  </div>
                )}

                {/* Status Badges */}
                <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                  <label className="block text-sm font-medium text-slate-500 dark:text-slate-400 mb-2">
                    Status
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <span className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                      currentProfile?.verified
                        ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                        : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                    }`}>
                      {currentProfile?.verified ? '✓ Verified' : '⏳ Pending Verification'}
                    </span>
                    {currentProfile?.featured && (
                      <span className="px-3 py-1.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg text-sm font-medium">
                        ⭐ Featured
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Edit Mode
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-slate-900 dark:text-white text-lg">Edit Profile</h3>
              </div>

              {editFormErrors.general && (
                <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                  {editFormErrors.general}
                </div>
              )}

              <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6 space-y-5">
                {/* Editable Fields */}
                {providerConfig.fields
                  .filter(field => field.name !== 'category')
                  .sort((a, b) => a.order - b.order)
                  .map(field => (
                    <UniversalField
                      key={field.id}
                      field={field}
                      value={editFormData[field.name]}
                      onChange={(value) => handleEditFieldChange(field.name, value)}
                      error={editFormErrors[field.name]}
                    />
                  ))}

                {/* Categories */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Service Categories (Select up to 4) *
                  </label>
                  <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900">
                    {providerConfig.categories.map(category => (
                      <button
                        key={category}
                        type="button"
                        onClick={() => toggleEditCategory(category)}
                        disabled={!editSelectedCategories.includes(category) && editSelectedCategories.length >= 4}
                        className={`px-3 py-2 text-sm rounded-lg border transition-colors text-left ${
                          editSelectedCategories.includes(category)
                            ? 'bg-emerald-100 dark:bg-emerald-900/30 border-emerald-500 text-emerald-700 dark:text-emerald-300'
                            : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:border-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed'
                        }`}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    {editFormErrors.category && (
                      <p className="text-sm text-red-600 dark:text-red-400">{editFormErrors.category}</p>
                    )}
                    <p className="text-xs text-slate-500 dark:text-slate-400 ml-auto">
                      {editSelectedCategories.length}/4 selected
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 justify-end pt-4 border-t border-slate-200 dark:border-slate-700">
                  <button
                    onClick={cancelEditingProfile}
                    disabled={savingProfile}
                    className="px-6 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveProfileChanges}
                    disabled={savingProfile}
                    className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {savingProfile ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'sent' && (
        <div className="animate-fade-in">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-3">
              <h3 className="font-bold text-slate-800 dark:text-white mb-4 text-lg">Proposal History</h3>
              {sentOffers.length === 0 ? (
                <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                  <p className="text-slate-500 dark:text-slate-400">No offers sent yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {sentOffers.map((offer, idx) => (
                    <div key={idx} className="p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">{offer.valueProp}</p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">Price: R{offer.price}</p>
                        </div>
                        <span className="px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs rounded">Pending</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'requests' && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 animate-fade-in">
          <div className="lg:col-span-1 space-y-4">
            <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700 sticky top-4">
              <h3 className="font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Credits & Budget
              </h3>
              
              <div className="space-y-4">
                <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                  <div className="text-xs text-emerald-600 dark:text-emerald-400 mb-1">Available Credits</div>
                  <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{budget.credits}</div>
                </div>
                
                <div className="p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                  <div className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">Usage Rates</div>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-slate-600 dark:text-slate-400">Per Offer</span>
                      <span className="font-medium text-slate-900 dark:text-white">{creditsConfig.costPerOffer} credits</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600 dark:text-slate-400">Per Response</span>
                      <span className="font-medium text-slate-900 dark:text-white">{creditsConfig.costPerResponse} credits</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-slate-400">Daily Spent</span>
                    <span className="font-medium text-slate-900 dark:text-white">{budget.spentToday}/{budget.dailyLimit}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-slate-400">Weekly Spent</span>
                    <span className="font-medium text-slate-900 dark:text-white">{budget.spentWeek}/{budget.weeklyLimit}</span>
                  </div>
                </div>
                
                <button
                  onClick={() => setShowBudgetModal(true)}
                  className="w-full py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 text-sm font-medium"
                >
                  Manage Budget
                </button>
                
                {creditsConfig.allowAdditionalPurchase && (
                  <button className="w-full py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium flex items-center justify-center gap-2">
                    <ShoppingCart className="h-4 w-4" />
                    Buy Credits
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-3 space-y-4">
            <h3 className="font-bold text-slate-900 dark:text-white text-lg">Available Requests</h3>
            {activeRequests.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                <p className="text-slate-500 dark:text-slate-400">No requests available</p>
              </div>
            ) : (
              activeRequests.map(request => {
                const matchingPresets = presetOffers.filter(p => 
                  p.category === request.category && p.active
                );
                
                return (
                  <div key={request.id} className="p-6 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                    <h4 className="font-semibold text-lg text-slate-900 dark:text-white mb-2">{request.title}</h4>
                    <p className="text-slate-600 dark:text-slate-400 mb-4">{request.description}</p>
                    
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      <span className="px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs rounded">
                        {request.category}
                      </span>
                      <span className="text-sm text-slate-500 dark:text-slate-400">Budget: R{request.budget}</span>
                      <span className="text-sm text-slate-500 dark:text-slate-400">• {request.timeframe}</span>
                    </div>

                    {matchingPresets.length > 0 && (
                      <div className="mb-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                        <div className="flex items-center gap-2 mb-2">
                          <Zap className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                          <span className="text-sm font-medium text-purple-900 dark:text-purple-100">Quick Response</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {matchingPresets.map(preset => (
                            <button
                              key={preset.id}
                              onClick={() => openOfferModal(request, preset)}
                              className="px-3 py-1.5 bg-white dark:bg-slate-800 border border-purple-300 dark:border-purple-700 rounded-lg text-sm hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-colors"
                            >
                              <div className="font-medium text-slate-900 dark:text-white">{preset.name}</div>
                              <div className="text-xs text-slate-500 dark:text-slate-400">R{preset.price} • {preset.timeframe}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex justify-between items-center">
                      <div className="text-sm text-slate-500 dark:text-slate-400">
                        {matchingPresets.length > 0 && (
                          <span className="text-purple-600 dark:text-purple-400">
                            {matchingPresets.length} preset{matchingPresets.length > 1 ? 's' : ''} available
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => openOfferModal(request)}
                        className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                      >
                        Custom Offer ({creditsConfig.costPerOffer} credits)
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {showPresetModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">{editingPreset ? 'Edit' : 'Create'} Preset Offer</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Preset Name</label>
                <input
                  type="text"
                  value={presetForm.name}
                  onChange={(e) => setPresetForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Standard Plumbing Service"
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Category</label>
                <select
                  value={presetForm.category}
                  onChange={(e) => setPresetForm(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                >
                  {selectedCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Value Proposition</label>
                <textarea
                  value={presetForm.valueProp}
                  onChange={(e) => setPresetForm(prev => ({ ...prev, valueProp: e.target.value }))}
                  placeholder="Your standard pitch for this type of request..."
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  rows={4}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Price (R)</label>
                  <input
                    type="number"
                    value={presetForm.price}
                    onChange={(e) => setPresetForm(prev => ({ ...prev, price: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Timeframe</label>
                  <input
                    type="text"
                    value={presetForm.timeframe}
                    onChange={(e) => setPresetForm(prev => ({ ...prev, timeframe: e.target.value }))}
                    placeholder="e.g., 2-3 days"
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Description (Optional)</label>
                <textarea
                  value={presetForm.description}
                  onChange={(e) => setPresetForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Additional notes about this preset..."
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  rows={2}
                />
              </div>

              <label className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={presetForm.autoRespond}
                  onChange={(e) => setPresetForm(prev => ({ ...prev, autoRespond: e.target.checked }))}
                  className="rounded"
                />
                <Zap className="h-5 w-5 text-amber-500" />
                <div className="flex-1">
                  <div className="font-medium text-slate-900 dark:text-white">Enable Auto-Respond</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400">
                    Automatically send this offer to matching requests ({creditsConfig.costPerOffer} credits per offer)
                  </div>
                </div>
              </label>

              <div className="flex gap-3 justify-end pt-4 border-t border-slate-200 dark:border-slate-700">
                <button
                  onClick={() => setShowPresetModal(false)}
                  className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={savePreset}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  {editingPreset ? 'Update' : 'Create'} Preset
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isOfferModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Send Offer</h3>
            
            {!selectedPreset && presetOffers.filter(p => p.active).length > 0 && (
              <div className="mb-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <div className="flex items-center gap-2 mb-2">
                  <Copy className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                  <span className="text-sm font-medium text-purple-900 dark:text-purple-100">Use a preset</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {presetOffers.filter(p => p.active).map(preset => (
                    <button
                      key={preset.id}
                      onClick={() => applyPresetToOffer(preset)}
                      className="px-3 py-1.5 bg-white dark:bg-slate-700 border border-purple-300 dark:border-purple-600 rounded-lg text-sm hover:bg-purple-50 dark:hover:bg-purple-900/30 text-slate-900 dark:text-white"
                    >
                      {preset.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedPreset && (
              <div className="mb-4 p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg border border-purple-300 dark:border-purple-700">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-purple-900 dark:text-purple-100">
                    Using preset: {selectedPreset.name}
                  </span>
                  <button
                    onClick={() => setSelectedPreset(null)}
                    className="text-sm text-purple-600 dark:text-purple-400 hover:underline"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Value Proposition</label>
                <textarea
                  value={offerForm.valueProp}
                  onChange={(e) => setOfferForm(prev => ({ ...prev, valueProp: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  rows={4}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Price (R)</label>
                  <input
                    type="number"
                    value={offerForm.price}
                    onChange={(e) => setOfferForm(prev => ({ ...prev, price: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Timeframe</label>
                  <input
                    type="text"
                    value={offerForm.timeframe}
                    onChange={(e) => setOfferForm(prev => ({ ...prev, timeframe: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  />
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setIsOfferModalOpen(false);
                    setSelectedPreset(null);
                  }}
                  className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={sendOffer}
                  disabled={isSendingOffer}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                >
                  {isSendingOffer ? 'Sending...' : `Send Offer (${creditsConfig.costPerOffer} credits)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showBudgetModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl max-w-md w-full p-6">
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Manage Budget</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Daily Limit</label>
                <input
                  type="number"
                  value={tempLimits.daily}
                  onChange={(e) => setTempLimits(prev => ({ ...prev, daily: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Weekly Limit</label>
                <input
                  type="number"
                  value={tempLimits.weekly}
                  onChange={(e) => setTempLimits(prev => ({ ...prev, weekly: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowBudgetModal(false)}
                  className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={saveBudgetSettings}
                  className="px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-lg"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderView;